import React, { useState } from 'react';
import { 
    DefaultButton, 
    Panel, 
    PanelType,
    IPanelStyles,
    Text,
    TextField,
    IColumn,
} from '@fluentui/react';
import LoaderComponent from '../loader/loader.component';
import ErrorMessageComponent from '../error-message/error-message.component';
import DynamicFormFieldComponent from '../dynamic-form-field/dynamic-form-field.component';
import TableDefaultComponent from '../table-default/table-default.component';
import ButtonPrimaryComponent from '../button-primary/button-primary.component';

import ApiService from '../../services/api/api.service';
import useAuthStatus from '../../hooks/auth/AuthHook';

import ItemFormulario from '../../models/ItemFormulario';
import ItemFormularioCampo from '../../models/ItemFormularioCampo';

const panelStyles : IPanelStyles = {
    root: {},
    overlay: {},
    hiddenPanel: {},
    main: {},
    commands: {},
    contentInner: {},
    scrollableContent: {},
    navigation: {},
    header: {},
    headerText: {},
    content: {},
    footer: {},
    footerInner: {},
    subComponentStyles: {
        closeButton: {
            root: {
                color: 'rgb(50, 49, 48)',
            },
        },
    },
};

const columns : IColumn[] = [
    {
        key: '1',
        name: 'Orden',
        fieldName: 'Orden',
        minWidth: 0,
        isResizable: true,
    },
    {
        key: '2',
        name: 'Nombre',
        fieldName: 'NombreCampo',
        minWidth: 0,
        isResizable: true,
    },
    {
        key: '3',
        name: 'Tipo de dato',
        fieldName: 'Tipo',
        minWidth: 0,
        isResizable: true,
    },
];

interface IDynamicFormComponentProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (form: ItemFormulario) => void;
    form?: ItemFormulario;
};

const DynamicFormComponent = (props: IDynamicFormComponentProps) => {
    const auth = useAuthStatus();
    
    const [form, setForm] = useState<ItemFormulario>(props.form ? props.form : new ItemFormulario());
    const [selectedIndexColumn, setSelectedIndexColumn] = useState<number>();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [newColumnOpen, setNewColumnOpen] = useState<boolean>(false);
    const [editColumnOpen, setEditColumnOpen] = useState<boolean>(false);

    const onChangeDescription = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) : void => {
        setForm({
            ...form,
            Descripcion: newValue || "",
        });
    };

    const onAddColumn = (formField: ItemFormularioCampo) : void => {
        setForm({
            ...form,
            Columnas: [
                ...form.Columnas,
                formField,
            ],
        });
    };

    const onEditColumn = (formField: ItemFormularioCampo) : void => {
        if (selectedIndexColumn !== undefined) {
            let fields : ItemFormularioCampo[] = form.Columnas;
            fields.splice(selectedIndexColumn, 1);
            setForm({
                ...form,
                Columnas: [
                    ...fields,
                    formField,
                ],
            });
        }
    };

    const onDeleteColumn = () : void => {
        if (selectedIndexColumn !== undefined) {
            let fields : ItemFormularioCampo[] = form.Columnas;
            fields.splice(selectedIndexColumn, 1);
            setForm({
                ...form,
                Columnas: [
                    ...fields,
                ],
            });
            setSelectedIndexColumn(undefined);
        }
    };

    const onDismiss = () : void => {
        setForm(new ItemFormulario());
        props.onClose();
    };

    const onSubmit = () : void => {
        if (auth.user) {
            setLoading(true);
            ApiService.Post<number>("OrdenesTrabajo", "SetFormulario", auth.user, {
                "ItemFormulario": form,
            }).then(() => {
                props.onConfirm(form);
                props.onClose();
                setLoading(false);
            }, (error: string) => {
                setError(error);
                setLoading(false);
            });
        }
    };

    return (
        <Panel 
            id="ModalCrearFormulario" 
            isOpen={props.isOpen} 
            onDismiss={onDismiss} 
            type={PanelType.largeFixed}
            headerText='Nuevo formulario'
            styles={panelStyles}>
            <div className="container-fluid p-0">
                <div className="row m-0">
                    <div className='col-12'>
                        <Text className="my-1 d-block">
                            Nombre del formulario:
                        </Text>
                        <TextField
                            onChange={onChangeDescription}
                            value={form.Descripcion} />
                    </div>
                    <div className='col-12'>
                        <Text className="my-1 d-block">
                            Campos:
                        </Text>
                    </div>
                    <div className='col-12'>
                        <ButtonPrimaryComponent
                            text="Añadir"
                            icon="AddTo"
                            className='me-2'
                            onClick={() => setNewColumnOpen(true)} />
                        <ButtonPrimaryComponent
                            text="Editar"
                            icon="Edit"
                            disabled={selectedIndexColumn === undefined}
                            className='me-2'
                            onClick={() => setEditColumnOpen(true)} />
                        <ButtonPrimaryComponent
                            text="Eliminar"
                            icon="ChromeClose"
                            disabled={selectedIndexColumn === undefined}
                            className='me-2'
                            onClick={onDeleteColumn} />
                    </div>
                    <div className='col-12'>
                        <TableDefaultComponent 
                            items={form.Columnas}
                            selectedItem={(selectedIndexColumn !== undefined) ? form.Columnas[selectedIndexColumn] : undefined}
                            columns={columns}
                            onRowClick={(item: ItemFormularioCampo, index: number) => setSelectedIndexColumn(index)}
                            onRowDoubleClick={(item: ItemFormularioCampo, index: number) => {
                                setSelectedIndexColumn(index);
                                setEditColumnOpen(true);
                            }} />
                    </div>
                    <div className='col-12 mt-3'>
                        <DefaultButton 
                            text='Aceptar'
                            iconProps={{
                                iconName: 'Save',
                            }}
                            onClick={onSubmit} />
                    </div>
                </div>
            </div>
            {
                newColumnOpen ? (
                    <DynamicFormFieldComponent 
                        isOpen={true} 
                        onClose={() => setNewColumnOpen(false)}
                        onConfirm={onAddColumn} />
                ) : (
                    null
                )
            }
            {
                (editColumnOpen && selectedIndexColumn !== undefined) ? (
                    <DynamicFormFieldComponent 
                        formField={form.Columnas[selectedIndexColumn]}
                        isOpen={true} 
                        onClose={() => setEditColumnOpen(false)}
                        onConfirm={onEditColumn} />
                ) : (
                    null
                )
            }
            <ErrorMessageComponent message={error} />
            <LoaderComponent loading={loading} />
        </Panel>
    );
};

export default DynamicFormComponent;
